import { ActivityComponentType } from '@stackflow/react'
import { useQueryClient } from '@tanstack/react-query'
import dynamic from 'next/dynamic'
import { useEffect, useMemo, useState } from 'react'
import { activityOrder, parseStringToObjects } from '../../../helpers/aiTutorActivityHelper'
import apis from '../../../modules/apis'
import { useLessonDetail } from '../../../modules/apis/student/ai-tutor'
import { useConversationSetting } from '../../../modules/apis/student/conversation'
import { axiosPost } from '../../../modules/axiosHelper'
import { useCurrentUser } from '../../../modules/context/UserContext'
import useAppRouter from '../../../modules/hooks/common/useAppRouter'
import useWakeLock from '../../../modules/hooks/common/useWakeLock'
import { n_ai_tutor } from '../../../modules/i18n/strings/auto/wording'
import useTranslation from '../../../modules/i18n/useTranslation'
import {
  aiTutorCourseDetail,
  aiTutorLessonDetail,
  aiTutorStreak,
  aiTutorSummary,
  aiTutorSummaryCourse,
} from '../../../modules/react-query/queryApis'
import LoadingSpinner from '../../common/LoadingSpinner'
import { Word } from '../../new-portal/ai-tutor/Common'
import AITutorActivityHeader from '../../new-portal/ai-tutor/activity/ActivityHeader'
import ChooseActivity from '../../new-portal/ai-tutor/activity/ChooseActivity'
import ExpressionActivity from '../../new-portal/ai-tutor/activity/ExpressionActivity'
import FillinActivity from '../../new-portal/ai-tutor/activity/FillinActivity'
import RepeatActivity from '../../new-portal/ai-tutor/activity/RepeatActivity'
import { RoleplayActivity } from '../../new-portal/ai-tutor/activity/RoleplayActivity'
import SpeakActivity from '../../new-portal/ai-tutor/activity/SpeakActivity'
import Done from '../../new-portal/ai-tutor/done/Done'
import WebviewStackContainer from '../common/WebviewStackContainer'

const AITutorAct = dynamic(() => import('../../new-portal/ai-tutor/AITutorAct'), {
  loading: () => (
    <div className="flex h-screen w-screen items-center justify-center">
      <LoadingSpinner lg show primary />
    </div>
  ),
})

const parseActivity = (act, { done, setDone, nextActivity, acts, addFail, currentActivityIdx, pronunciation }) => {
  if (act.type === 'FillInTheBlankSpeakingActivity') {
    const FillInTheBlankSpeakingActivity = ({ idx, retry }) => (
      <SpeakActivity
        key={idx}
        idx={idx}
        retry={retry}
        done={done}
        setDone={setDone}
        nextActivity={nextActivity}
        content={parseStringToObjects(act.sentence, act.blanks)}
        audio_url={act[`audio_url_${pronunciation}`] || act.audio_url}
        addFail={addFail}
        textKo={act.sentence_kor}
        wordCount={act?.sentence?.split?.(' ')?.length}
      />
    )
    return FillInTheBlankSpeakingActivity
  }

  if (act.type === 'FillInTheBlankActivity') {
    const FillInTheBlankActivity = ({ idx, retry }) => (
      <FillinActivity
        key={idx}
        content={parseStringToObjects(act.sentence, act.blanks)}
        nextActivity={nextActivity}
        addFail={addFail}
        retry={retry}
        idx={idx}
      />
    )
    return FillInTheBlankActivity
  }

  if (act.type === 'ChooseCorrectSentenceActivity') {
    const content =
      Math.random() > 0.5
        ? [
            { content: act.right, correct: true },
            {
              content: act.wrong,
              correct: false,
              explanation: act.explanation,
              explanation_kor: act.explanation_kor,
              explanation_ja: act.explanation_ja,
            },
          ]
        : [
            {
              content: act.wrong,
              correct: false,
              explanation: act.explanation,
              explanation_kor: act.explanation_kor,
              explanation_ja: act.explanation_ja,
            },
            { content: act.right, correct: true },
          ]
    const ChooseCorrectSentenceActivity = ({ idx, retry }) => (
      <ChooseActivity
        key={idx}
        idx={idx}
        retry={retry}
        done={done}
        setDone={setDone}
        nextActivity={nextActivity}
        content={content}
        context={act.situation}
        context_kor={act.situation_kor}
        context_ja={act.situation_ja}
        addFail={addFail}
      />
    )
    return ChooseCorrectSentenceActivity
  }

  if (act.type === 'ListenAndRepeatActivity') {
    const ListenAndRepeatActivity = ({ idx, retry }) => (
      <RepeatActivity
        key={idx}
        idx={idx}
        retry={retry}
        done={done}
        setDone={setDone}
        nextActivity={nextActivity}
        content={parseStringToObjects(act.sentence, act.blanks)}
        audio_url={act[`audio_url_${pronunciation}`] || act.audio_url}
        addFail={addFail}
        wordCount={act?.sentence?.split?.(' ')?.length}
      />
    )
    return ListenAndRepeatActivity
  }

  if (act.type === 'ExplanationActivity') {
    const ExpressionActivityRet = ({ idx, retry, t, subtitleLang }) => (
      <ExpressionActivity
        key={idx}
        isActive={currentActivityIdx === idx}
        nextActivity={nextActivity}
        expressions={[
          {
            title: t(n_ai_tutor.study_activity.label1),
            type: t(n_ai_tutor.study_activity.label1),
            content: act.expression,
            audio:
              act[`expression_explanation_audio_url_${pronunciation}`] ||
              act.expression_explanation_audio_url ||
              'https://conversation-audio-tts.s3.ap-northeast-2.amazonaws.com/34/assistant-0.wav',
            script: {
              ko: act.expression_explanation_kor,
              en: act.expression_explanation,
              ja: act.expression_explanation_ja,
            },
          },
          ...act.examples.map((e, i) => ({
            title: `${t(n_ai_tutor.study_activity.label2)} ${i + 1}`,
            type: `${t(n_ai_tutor.study_activity.label2)} ${i + 1}`,
            content: e,
            audio:
              act[`expression_scripts_audio_url_${pronunciation}`]?.[i] ||
              act.expression_scripts_audio_url?.[i] ||
              'https://conversation-audio-tts.s3.ap-northeast-2.amazonaws.com/34/assistant-0.wav',
            script: {
              ko: act.example_scripts_kor?.[i],
              en: act.example_scripts?.[i],
              ja: act.example_scripts_ja?.[i],
            },
          })),
        ]}
        subtitleLang={subtitleLang}
      />
    )
    return ExpressionActivityRet
  }

  if (act.type === 'MiniRoleplayActivity') {
    const roleplay = [
      {
        by: 'context',
        content: act.situation,
        // translated: act.situation_kor,
        content_kr: act.situation_kor,
        content_ja: act.situation_ja,
        audio_url: '',
      },
      ...(act?.turns
        ? act.turns.map((item) => {
            return {
              by: item.speaker === 'student' ? 'user' : 'ai',
              content: parseStringToObjects(item.dialogue, item.speaker === 'student' ? item.blanks : []) as Word[],
              // translated: item.dialogue_kr,
              content_kr: item.dialogue_kr,
              content_ja: item.dialogue_ja,
              audio_url: item.speaker === 'student' ? null : item[`audio_url_${pronunciation}`] || item.audio_url,
              wordCount: item.dialogue.split(' ').length,
            }
          })
        : []),
    ]

    const RoleplayActivityRet = ({ idx, retry }) => (
      <RoleplayActivity
        idx={idx}
        key={idx}
        retry={retry}
        roleplay={roleplay}
        done={done}
        setDone={setDone}
        nextActivity={nextActivity}
        userRole={act?.roles?.student}
        aiRole={act?.roles?.ai_tutor}
        addFail={addFail}
      />
    )
    return RoleplayActivityRet
  }

  return () => null
}

const AITutorActActivity: ActivityComponentType = () => {
  const router = useAppRouter()
  const tabId = router.query?.tabId
  const [activity, setActivity] = useState(+tabId || 0)
  const lessonId = router.query?.lessonId
  const [done, setDone] = useState(false)
  const [startTime, setStartTime] = useState(0)
  const [endTime, setEndTime] = useState(0)
  const { data: lessonData } = useLessonDetail(lessonId)
  const [fail, setFail] = useState([])
  const [nextFail, setNextFail] = useState(null)
  const queryClient = useQueryClient()
  const { data: lesson } = useLessonDetail(+lessonId)
  const { t, locale } = useTranslation()
  const [currentView, setCurrentView] = useState(null)
  const [subtitleLang, setSubtitleLang] = useState('ko')
  const [lastProgress, setLastProgress] = useState(0)
  const [prevLesson, setPrevLesson] = useState({} as typeof lessonData)
  const setting = useConversationSetting()

  const { currentUser } = useCurrentUser()
  useWakeLock()

  const nextActivity = () => {
    setNextFail((f) => {
      if (f?.comp) setFail((fail) => [...fail, f.comp])
      return null
    })
    setActivity((activity) => activity + 1)
    setDone(false)
  }

  const addFail = () => {
    setCurrentView((v) => {
      setNextFail(v)
      return v
    })
  }

  const activities = useMemo(() => {
    const data = prevLesson?.lesson?.content?.activities?.filter((item) => item.type)
    const acts = data?.sort((a, b) => {
      return activityOrder.indexOf(a.type) - activityOrder.indexOf(b.type)
    })

    return (
      acts?.map((act) =>
        parseActivity(act, {
          done,
          setDone,
          nextActivity,
          acts,
          addFail,
          currentActivityIdx: activity,
          pronunciation: setting?.data?.pronunciation,
        })
      ) || []
    )
  }, [prevLesson, activity, setting?.data?.pronunciation])

  const views = useMemo(() => {
    const acts = activities.slice(0, -1)
    const roleplays = activities.slice(-1)

    return [
      ...acts.map((comp) => ({ comp, retry: false })),
      ...fail.map((comp) => ({ comp, retry: true })),
      ...roleplays.map((comp) => ({ comp, retry: false })),
    ]
  }, [activities, fail])

  const renders = useMemo(
    () =>
      views.map(({ comp, retry }, idx) => {
        return comp({ idx, retry, subtitleLang, t })
      }),
    [views, subtitleLang, t]
  )

  const currentActivityList = useMemo(() => {
    const data = prevLesson?.lesson?.content?.activities?.filter((item) => item.type)
    const acts = data?.sort((a, b) => {
      return activityOrder.indexOf(a.type) - activityOrder.indexOf(b.type)
    })
    const activities = acts?.slice(0, -1) || []
    const roleplays = acts?.slice(-1) || []

    return [...activities, ...fail, ...roleplays]
  }, [prevLesson, fail])

  useEffect(() => {
    if (!views.length) return
    setCurrentView(views[activity])
  }, [activity, views])

  useEffect(() => {
    if (locale === 'ko') {
      setSubtitleLang('ko')
    } else if (locale === 'ja') {
      // setSubtitleLang('ja')
      setSubtitleLang('en')
    } else {
      setSubtitleLang('en')
    }
  }, [locale])

  useEffect(() => {
    setActivity(+tabId || 0)
    setFail([])
    setStartTime(Date.now())
  }, [router.query?.lessonId])

  useEffect(() => {
    if (prevLesson?.lesson?.id !== lessonData?.lesson?.id) setPrevLesson(lessonData)
  }, [lessonData])

  useEffect(() => {
    if (activities.length)
      if (lastProgress !== activity) {
        setLastProgress(activity)
        axiosPost(apis.aiTutor.lesson.progress({ lessonId }), {
          activity_index: Math.min(activity, activities.length),
          activity_count: activities.length,
        }).then((r) => {
          if (activity >= activities.length) {
            queryClient.invalidateQueries(aiTutorLessonDetail.getQueryKey({ pathParams: { lessonId: +lessonId } }))
            queryClient.invalidateQueries(
              aiTutorCourseDetail.getQueryKey({ pathParams: { courseId: lesson?.lesson?.ai_tutor_course_id } })
            )
            queryClient.invalidateQueries(
              aiTutorCourseDetail.getQueryKey({ pathParams: { courseId: 'latest' as any } })
            )
          }
        })
      }
  }, [activity, activities, lastProgress])

  useEffect(() => {
    return () => {
      queryClient.invalidateQueries(aiTutorLessonDetail.getQueryKey({ pathParams: { lessonId: +lessonId } }))
      queryClient.invalidateQueries(aiTutorStreak.getQueryKey())
      queryClient.invalidateQueries(aiTutorSummary.getQueryKey())
      queryClient.invalidateQueries(aiTutorSummaryCourse.getQueryKey())
    }
  }, [lessonId])

  useEffect(() => {
    return () => {
      queryClient.invalidateQueries(
        aiTutorCourseDetail.getQueryKey({ pathParams: { courseId: lesson?.lesson?.ai_tutor_course_id } })
      )
      queryClient.invalidateQueries(aiTutorCourseDetail.getQueryKey({ pathParams: { courseId: 'latest' as any } }))
    }
  }, [lesson?.lesson?.ai_tutor_course_id])

  useEffect(() => {
    if (activity >= views.length && activity && views.length) {
      if (!endTime) setEndTime(Date.now())
    } else {
      setEndTime(null)
    }
  }, [activity, views, endTime])

  return (
    <WebviewStackContainer
      key={lesson?.id}
      backgroundColor={activity === 0 ? 'rgb(239 241 249)' : undefined}
      height={endTime ? '0px' : '52px'}
      title={
        !endTime && (
          <AITutorActivityHeader
            progress={activity}
            subtitleLang={subtitleLang}
            setSubtitleLang={setSubtitleLang}
            activityList={currentActivityList}
            setActivity={setActivity}
          />
        )
      }
      border={false}
    >
      {endTime ? (
        <>
          <Done startTime={startTime} endTime={endTime} lesson={lesson?.lesson} />
        </>
      ) : renders?.length ? (
        <AITutorAct activities={renders} activity={activity} setActivity={setActivity} done={done} setDone={setDone} />
      ) : (
        <div className="flex flex-col flex-1 items-center justify-center">
          <LoadingSpinner lg show primary />
        </div>
      )}
    </WebviewStackContainer>
  )
}

export default AITutorActActivity
